import { Pog } from "@CommonTypes/merchflow/pog/pog";
import { useEffect, useRef, useState } from "react";
import { Flex } from "src/elements";
import { useTranslation } from "src/i18n";
import { animate, initializeScene } from "./store/utils/three";
import { Bays } from "./components/Bays";
import { SplitShiftProvider } from "./store/splitShift";
import { ThreePlanogram } from "./store/types";
import { useAtom, useSetAtom } from "jotai";
import { planogram3dCameraStyleAtom } from "./store/atoms";
import { create3dCamera, createFrontCamera } from "./store/utils/cameras";
import * as THREE from "three";
import {
  planogramHoveredItemAtom,
  planogramHoveredShelfAtom,
  planogramSelectedItemsAtom,
} from "../PlanogramExtensions/store/atoms";
import { PlanogramItem } from "@CommonTypes/merchflow/pog/item";
import { PlanogramShelf } from "@CommonTypes/merchflow/pog/shelf";
import { useShowDividers } from "../Planogram/common/shelfDividersView";
import {
  itemFrontOverhangMarker,
  OVERHANG_COLOR,
  OVERHANG_INVALID_COLOR,
  shelfFrontOverhangMarker,
} from "../Planogram/common/frontOverhangView";
import styled from "styled-components";

interface Props {
  pog: Pog;
}

const raycaster = new THREE.Raycaster();
const mouse = new THREE.Vector2();

const OverhangLegend = styled.div`
  position: absolute;
  left: 12px;
  bottom: 12px;
  z-index: 1;
  pointer-events: none;
  padding: 8px 10px;
  border-radius: 4px;
  background: rgba(255, 255, 255, 0.92);
  color: #1f2937;
  font-size: 12px;
  line-height: 18px;
  box-shadow: 0 1px 4px rgba(0, 0, 0, 0.18);
`;

const LegendSwatch = styled.span<{ $invalid: boolean }>`
  display: inline-block;
  width: 9px;
  height: 9px;
  margin-right: 6px;
  border-radius: 2px;
  background: ${({ $invalid }) => ($invalid ? OVERHANG_INVALID_COLOR : OVERHANG_COLOR)};
`;

/**
 * Shared raycast helper
 */
const getRaycastHit = (
  event: MouseEvent,
  camera: THREE.Camera,
  renderer: THREE.WebGLRenderer,
  scene: THREE.Scene,
): THREE.Intersection | undefined => {
  const rect = renderer.domElement.getBoundingClientRect();
  mouse.x = ((event.clientX - rect.left) / rect.width) * 2 - 1;
  mouse.y = -((event.clientY - rect.top) / rect.height) * 2 + 1;

  raycaster.setFromCamera(mouse, camera);

  return raycaster
    .intersectObjects(scene.children, true)
    .find((i) => i.object.userData?.type !== undefined);
};

export const Planogram3d = ({ pog }: Props) => {
  const setHoveredItem = useSetAtom(planogramHoveredItemAtom);
  const setHoveredShelf = useSetAtom(planogramHoveredShelfAtom);
  const setSelectedItems = useSetAtom(planogramSelectedItemsAtom);
  const [cameraStyle] = useAtom(planogram3dCameraStyleAtom);
  const [three, setThree] = useState<ThreePlanogram | null>(null);
  const { showDividers } = useShowDividers(pog);
  const { t } = useTranslation("common");
  const shelves = pog.planogram.bays.flatMap((bay) => bay.shelves);
  const shelfMarkers = shelves
    .map((shelf) => shelfFrontOverhangMarker(shelf))
    .filter((marker) => marker !== null);
  const itemMarkers = shelves
    .flatMap((shelf) => shelf.items)
    .map((item) => itemFrontOverhangMarker(item))
    .filter((marker) => marker !== null);

  const refContainer = useRef<HTMLDivElement>(null);
  const refCleanup = useRef<() => void>(null);
  const refCamera = useRef<THREE.Camera>(null);
  const refUpdateMovement = useRef<() => void>(null);

  // Mount handler.
  useEffect(() => {
    if (!refContainer.current) return;

    const { scene, renderer, camera, cleanup } = initializeScene(
      refContainer.current,
      cameraStyle,
      pog,
    );

    setThree({ renderer, scene });
    refCamera.current = camera;
    refCleanup.current = cleanup;

    const stopAnimation = animate(
      renderer,
      scene,
      () => refCamera.current!,
      () => {
        refUpdateMovement.current?.();
      },
    );

    return () => {
      stopAnimation();
      refCleanup.current?.();
      renderer.dispose();
    };
  }, []);

  // Camera style handler.
  useEffect(() => {
    if (!three?.renderer || !refContainer.current) return;

    refCleanup.current?.();

    let camera: THREE.Camera;
    let cleanup: () => void;
    let updateMovement: (() => void) | null = null;

    if (cameraStyle === "FRONT") {
      const result = createFrontCamera(refContainer.current, pog);
      camera = result.camera;
      cleanup = result.cleanup;
    } else {
      const result = create3dCamera(three.renderer, refContainer.current, pog);
      camera = result.camera;
      cleanup = result.cleanup;
      updateMovement = result.updateMovement;
    }

    refCamera.current = camera;
    refCleanup.current = cleanup;
    refUpdateMovement.current = updateMovement;
  }, [cameraStyle, three, pog]);

  // Hover handler.
  useEffect(() => {
    if (!three) return;

    let lastHovered: THREE.Mesh | null = null;

    const handleMouseMove = (event: MouseEvent) => {
      if (!refCamera.current) return;

      const hit = getRaycastHit(event, refCamera.current, three.renderer, three.scene);

      if (!hit) {
        setHoveredItem(null);
        setHoveredShelf(null);
        lastHovered = null;
        return;
      }

      if (hit.object === lastHovered) return;

      const mesh = hit.object as THREE.Mesh;

      if ("item" in mesh.userData) {
        setHoveredItem(mesh.userData.item as PlanogramItem);
        setHoveredShelf(null);
      } else if ("shelf" in mesh.userData) {
        setHoveredItem(null);
        setHoveredShelf(mesh.userData.shelf as PlanogramShelf);
      }

      lastHovered = mesh;
    };

    three.renderer.domElement.addEventListener("mousemove", handleMouseMove);

    return () => {
      three.renderer.domElement.removeEventListener("mousemove", handleMouseMove);
    };
  }, [three, setHoveredItem, setHoveredShelf]);

  // Click handler.
  useEffect(() => {
    if (!three) return;

    const handleClick = (event: MouseEvent) => {
      if (!refCamera.current) return;

      const hit = getRaycastHit(event, refCamera.current, three.renderer, three.scene);

      if (!hit) return;

      const mesh = hit.object as THREE.Mesh;

      if ("item" in mesh.userData) {
        setSelectedItems([mesh.userData.item as PlanogramItem]);
      }
    };

    three.renderer.domElement.addEventListener("click", handleClick);

    return () => {
      three.renderer.domElement.removeEventListener("click", handleClick);
    };
  }, [three, setSelectedItems]);

  // Resize handler.
  useEffect(() => {
    const onResize = () => {
      if (!refContainer.current || !three) return;

      const width = refContainer.current.clientWidth;
      const height = refContainer.current.clientHeight;

      three.renderer.setSize(width, height, false);
    };

    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, [three]);

  return (
    <Flex position="relative" flexGrow overflow="hidden">
      <Flex ref={refContainer} flexGrow overflow="hidden">
        {three && (
          <SplitShiftProvider bays={pog.planogram.bays}>
            <Bays bays={pog.planogram.bays} showDividers={showDividers} three={three} />
          </SplitShiftProvider>
        )}
      </Flex>
      {(shelfMarkers.length > 0 || itemMarkers.length > 0) && (
        <OverhangLegend aria-label={t("frontOverhang.legend.label")}>
          {shelfMarkers.length > 0 && (
            <div>
              <LegendSwatch $invalid={shelfMarkers.some((marker) => marker.invalid)} />
              {t(
                shelfMarkers.some((marker) => marker.invalid)
                  ? "frontOverhang.legend.shelfCheck"
                  : "frontOverhang.legend.shelfAllowed",
              )}
            </div>
          )}
          {itemMarkers.length > 0 && (
            <div>
              <LegendSwatch $invalid={itemMarkers.some((marker) => marker.invalid)} />
              {t(
                itemMarkers.some((marker) => marker.invalid)
                  ? "frontOverhang.legend.productInvalid"
                  : "frontOverhang.legend.product",
                {
                  label:
                    itemMarkers.length === 1
                      ? itemMarkers[0].label
                      : t("frontOverhang.legend.severalProducts"),
                },
              )}
            </div>
          )}
        </OverhangLegend>
      )}
    </Flex>
  );
};
